import { computed, shallowRef } from 'vue';
import type { ComputedRef, Ref, ShallowRef } from 'vue';

import type { MapFeature } from '@/components/map/features/MapFeatures';
import {
	asTypedMapFeature,
	getFeatureType,
} from '@/components/map/utils/mapFeatureGuards';
import type { FeatureData, FeatureType, TypedMapFeature } from '@/components/map/types/mapFeatureTypes';


export type UseHoveredFeatureResult = {
	hovered: ShallowRef<MapFeature | null>;
	hoveredTyped: ComputedRef<TypedMapFeature | null>;
	hoveredFeatureData: ComputedRef<FeatureData | null>;
	hoveredFeatureType: ComputedRef<FeatureType | null>;
	isMapHovered: ComputedRef<boolean>;
};


export function useHoveredFeature(
	allowedClickTypes: Ref<readonly FeatureType[]>,
): UseHoveredFeatureResult {
	const hovered = shallowRef<MapFeature | null>(null);
	const hoveredTyped = computed(() => {
		const feature = hovered.value;
		if (feature == null) {
			return null;
		}
		return asTypedMapFeature(feature);
	});
	const hoveredFeatureData = computed((): FeatureData | null => (
		hoveredTyped.value?.featureData ?? null
	));
	const isMapHovered = computed((): boolean => {
		const hoveredFeature = hovered.value;
		if (hoveredFeature == null) {
			return false;
		}
		const featureType = getFeatureType(hoveredFeature);
		return featureType != null && allowedClickTypes.value.includes(featureType);
	});
	const hoveredFeatureType = computed((): FeatureType | null => (
		hoveredTyped.value?.type ?? null
	));

	return {
		hovered,
		hoveredTyped,
		hoveredFeatureData,
		hoveredFeatureType,
		isMapHovered,
	};
}
